import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users, KeyRound, Copy, X, ShieldAlert, Pencil } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/team/agents");
      setAgents(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (forbidden) {
    return (
      <div className="px-6 lg:px-10 py-8" data-testid="team-page-forbidden">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Admin · Settings</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Team</h1>
        </div>
        <div className="border border-neutral-200 p-8 max-w-2xl flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-neutral-500 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="font-serif text-xl mb-1">Admin only</div>
            <p className="text-sm text-neutral-600">
              Only the brokerage admin can manage team members. Contact your brokerage admin if you&apos;ve forgotten your password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="team-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Admin · Settings</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Team</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
          Agents on your brokerage. Edit agent profile details or reset their passwords if they&apos;re locked out.
        </p>
      </div>

      <div className="border border-neutral-200" data-testid="team-table">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-neutral-200 bg-neutral-50 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          <div className="col-span-4 flex items-center gap-1.5"><Users className="h-3 w-3" strokeWidth={1.5} /> Agent</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-5 py-8 text-sm text-neutral-500">Loading…</div>
        ) : agents.length === 0 ? (
          <div className="px-5 py-8 text-sm text-neutral-500">No agents found.</div>
        ) : (
          agents.map((a) => {
            const isSelf = a.id === user?.id;
            return (
              <div
                key={a.id}
                className="grid grid-cols-12 gap-2 px-5 py-4 border-b border-neutral-200 last:border-b-0 items-center"
                data-testid={`team-row-${a.id}`}
              >
                <div className="col-span-4">
                  <div className="font-serif text-base">{a.full_name}</div>
                  {a.license_no && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 mt-0.5">Lic #{a.license_no}</div>
                  )}
                </div>
                <div className="col-span-4 text-sm font-mono text-neutral-700 break-all">{a.email}</div>
                <div className="col-span-1 text-xs uppercase tracking-[0.2em] font-mono text-neutral-600">
                  {a.role === "admin" ? "Admin" : "Agent"}
                  {isSelf && <span className="ml-2 text-[10px] text-neutral-400">(you)</span>}
                </div>
                <div className="col-span-3 text-right flex justify-end gap-2">
                  {!isSelf && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-neutral-300 hover:bg-neutral-100"
                        onClick={() => setEditTarget(a)}
                        data-testid={`edit-agent-button-${a.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none border-neutral-300 hover:bg-neutral-100"
                        onClick={() => setResetTarget(a)}
                        data-testid={`reset-password-button-${a.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                        Reset password
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {resetTarget && (
        <ResetPasswordDialog
          agent={resetTarget}
          onClose={() => { setResetTarget(null); setResetResult(null); }}
          onDone={(result) => setResetResult(result)}
          result={resetResult}
        />
      )}
      {editTarget && (
        <EditAgentDialog
          agent={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => { setEditTarget(null); await load(); }}
        />
      )}
    </div>
  );
}

function DialogShell({ eyebrow, title, subtitle, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid={testId}>
      <div className="bg-white border border-neutral-300 max-w-lg w-full p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">{eyebrow}</div>
            <h2 className="font-serif text-2xl">{title}</h2>
            {subtitle && <div className="font-mono text-xs text-neutral-500 mt-1">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900" aria-label="Close" data-testid={`${testId}-close`}>
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditAgentDialog({ agent, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: agent.full_name || "",
    email: agent.email || "",
    license_no: agent.license_no || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/team/agents/${agent.id}`, {
        full_name: form.full_name,
        email: form.email.toLowerCase().trim(),
        license_no: form.license_no,
      });
      toast.success("Agent updated");
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900";

  return (
    <DialogShell
      eyebrow="Edit Agent"
      title={agent.full_name}
      subtitle={`Current: ${agent.email}`}
      onClose={onClose}
      testId="edit-agent-dialog"
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Full Name</Label>
          <Input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            required
            className={inputCls}
            data-testid="edit-agent-name-input"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email Address</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className={`${inputCls} font-mono`}
            data-testid="edit-agent-email-input"
          />
          <p className="text-xs text-neutral-500 mt-1">The agent will sign in with this new email going forward.</p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">License # (optional)</Label>
          <Input
            value={form.license_no}
            onChange={(e) => setForm({ ...form, license_no: e.target.value })}
            className={`${inputCls} font-mono`}
            data-testid="edit-agent-license-input"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-neutral-300">
            Cancel
          </Button>
          <Button type="submit" disabled={saving} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="edit-agent-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

function ResetPasswordDialog({ agent, onClose, onDone, result }) {
  const [customPw, setCustomPw] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const res = await api.post(`/team/agents/${agent.id}/reset-password`, {
        new_password: customPw.trim() || null,
      });
      onDone(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(result.new_password);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select the password and copy manually.");
    }
  };

  return (
    <DialogShell
      eyebrow="Reset Password"
      title={agent.full_name}
      subtitle={agent.email}
      onClose={onClose}
      testId="reset-password-dialog"
    >
      {!result ? (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-neutral-600">
            Generate a new temporary password for this agent. Their current password will stop working immediately. Share the new password through a secure channel — they should sign in and change it right away.
          </p>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Custom password (optional)</Label>
            <Input
              type="text"
              value={customPw}
              onChange={(e) => setCustomPw(e.target.value)}
              placeholder="Leave blank to auto-generate"
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono"
              data-testid="custom-password-input"
            />
            <p className="text-xs text-neutral-500 mt-1">If blank, a secure 12-character password will be generated.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-neutral-300">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="reset-password-confirm">
              <KeyRound className="h-4 w-4 mr-2" strokeWidth={1.5} />
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4" data-testid="reset-password-result">
          <p className="text-sm text-neutral-600">
            Password has been reset. Share this with <span className="font-semibold text-neutral-900">{result.agent_name}</span> securely.
          </p>
          <div className="border border-neutral-300 bg-neutral-50 p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">New Password</div>
            <div className="flex items-center gap-3">
              <code className="font-mono text-lg text-neutral-900 break-all flex-1" data-testid="reset-password-value">{result.new_password}</code>
              <Button type="button" size="sm" onClick={copyPassword} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white shrink-0" data-testid="copy-password-button">
                <Copy className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                Copy
              </Button>
            </div>
          </div>
          <div className="text-xs text-neutral-500 border-l-2 border-amber-500 pl-3 py-1">
            This password will not be shown again. The agent should change it after signing in via <span className="font-semibold">Account Security</span>.
          </div>
          <div className="flex justify-end pt-2 border-t border-neutral-200">
            <Button onClick={onClose} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white">Done</Button>
          </div>
        </div>
      )}
    </DialogShell>
  );
}
